import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../stores';
import { getItinerariesPage } from '../../services';
import { ItineraryListItem } from '../../types';
import { DestinationPassport } from '../plans';
import './ProfilePanel.css';

interface ProfilePanelProps {
  onClose: () => void;
}

const ProfilePanel: React.FC<ProfilePanelProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const logout = useUserStore((s) => s.logout);
  const [plans, setPlans] = useState<ItineraryListItem[]>([]);
  // The envelope's total over every plan, not a sum of this page's rows.
  const [timesUsed, setTimesUsed] = useState(0);

  useEffect(() => {
    getItinerariesPage()
      .then(page => {
        setPlans(Array.isArray(page.itineraries) ? page.itineraries : []);
        setTimesUsed(page.times_used);
      })
      .catch(() => { setPlans([]); setTimesUsed(0); });
  }, []);

  if (!user) return null;

  const initials = `${(user.first_name || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase() || '?';
  const sharedCount = plans.filter(p => (p as { visibility?: string }).visibility === 'public').length;

  const active = plans.filter(p => p.lifecycle_state === 'upcoming' || p.lifecycle_state === 'in_progress' || p.lifecycle_state === 'saved');
  const completed = plans.filter(p => p.lifecycle_state === 'completed');

  const handleLogout = async () => {
    await logout();
    onClose();
    navigate('/');
  };

  return (
    <div className="profile-panel-backdrop" onClick={onClose}>
      <div className="profile-panel" onClick={e => e.stopPropagation()}>
        <div className="profile-panel-header">
          <span className="profile-panel-title">Profile</span>
          <button className="profile-panel-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="profile-panel-user">
          <div className="profile-panel-avatar">{initials}</div>
          <div className="profile-panel-name">{user.first_name || user.email.split('@')[0]}</div>
          <div className="profile-panel-email">{user.email}</div>
        </div>

        <div className="profile-panel-stats">
          <div className="profile-panel-stat">
            <div className="profile-panel-stat-num">{plans.length}</div>
            <div className="profile-panel-stat-label">Plans</div>
          </div>
          <div className="profile-panel-stat">
            <div className="profile-panel-stat-num">{sharedCount}</div>
            <div className="profile-panel-stat-label">Shared</div>
          </div>
          {timesUsed > 0 && (
            <div className="profile-panel-stat">
              <div className="profile-panel-stat-num">{timesUsed}</div>
              <div className="profile-panel-stat-label">Times used</div>
            </div>
          )}
        </div>

        {/* Destination Passport */}
        {completed.length > 0 && (
          <div className="profile-panel-section">
            <DestinationPassport plans={completed} defaultShowMap={false} />
          </div>
        )}

        {/* Plans list */}
        {active.length > 0 && (
          <div className="profile-panel-section">
            <div className="profile-panel-section-label">Active & Draft</div>
            {active.map(p => (
              <button key={p.id} className="profile-panel-plan" onClick={() => { onClose(); navigate(`/itinerary/${p.id}`); }}>
                <span className="profile-panel-plan-title">{p.title}</span>
                <span className="profile-panel-plan-meta">{p.destination}</span>
              </button>
            ))}
          </div>
        )}
        {completed.length > 0 && (
          <div className="profile-panel-section">
            <div className="profile-panel-section-label">Completed</div>
            {completed.slice(0, 5).map(p => (
              <button key={p.id} className="profile-panel-plan profile-panel-plan--completed" onClick={() => { onClose(); navigate(`/itinerary/${p.id}`); }}>
                <span className="profile-panel-plan-title">{p.title}</span>
                <span className="profile-panel-plan-meta">{p.destination}</span>
              </button>
            ))}
          </div>
        )}

        <div className="profile-panel-actions">
          {/* Entry point for Pods — the nav tab only appears once you have one */}
          <button
            className="profile-panel-plan"
            onClick={() => { onClose(); navigate('/pods'); }}
          >
            <span className="profile-panel-plan-title">Pods</span>
            <span className="profile-panel-plan-meta">Plan with a group you keep</span>
          </button>
          <button className="profile-panel-logout" onClick={handleLogout}>Sign Out</button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePanel;
